import { NgTemplateOutlet } from '@angular/common';
import { booleanAttribute, ChangeDetectionStrategy, Component, inject, input, numberAttribute, output, viewChild } from '@angular/core';
import { MediaDto } from '@app/data/dto';
import { ButtonComponent } from '@app/shared/controls/app-button/app-button';
import { AppFiledrop, AppFiledropRejectedFile, AppFiledropUploadCompletedEvent, AppFiledropUploadFailedEvent, AppFiledropUploadFileEvent, AppFiledropUploadProgressEvent } from '@app/shared/controls/app-filedrop/app-filedrop';
import { TranslateService } from '@ngx-translate/core';
import { AppIconNameDesc } from "../app-icon-name-desc/app-icon-name-desc";

type AppModalConfirmButtonType = 'primary' | 'danger';
type AppModalExtraButtonType = 'primary' | 'secondary' | 'danger';

@Component({
  selector: 'app-modal',
  imports: [AppFiledrop, ButtonComponent, NgTemplateOutlet, AppIconNameDesc],
  templateUrl: './app-modal.html',
  styleUrl: './app-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppModal {
  private readonly translate = inject(TranslateService);
  private readonly filedrop = viewChild<AppFiledrop>('modalFiledrop');
  
  readonly icon = input('');
  readonly title = input('');
  readonly subtitle = input('');
  readonly ariaLabel = input('');
  readonly showHeader = input(true, { transform: booleanAttribute });
  readonly showFooter = input(true, { transform: booleanAttribute });
  readonly showConfirmButton = input(false, { transform: booleanAttribute });
  readonly showExtraButton = input(false, { transform: booleanAttribute });
  readonly closeOnBackdrop = input(true, { transform: booleanAttribute });
  readonly allowOverflow = input(false, { transform: booleanAttribute });
  readonly confirmDisabled = input(false, { transform: booleanAttribute });
  readonly closeDisabled = input(false, { transform: booleanAttribute });
  readonly extraDisabled = input(false, { transform: booleanAttribute });
  readonly confirmButtonText = input(this.translate.instant('common.actions.confirm'));
  readonly confirmButtonType = input<AppModalConfirmButtonType>('primary');
  readonly confirmButtonIcon = input<string | undefined>(undefined);
  readonly closeButtonText = input(this.translate.instant('common.actions.close'));
  readonly extraButtonText = input(this.translate.instant('shared.modal.extraButton'));
  readonly extraButtonType = input<AppModalExtraButtonType>('secondary');
  readonly width = input('min(70rem, 100%)');
  readonly height = input('auto');
  readonly minHeight = input('auto');
  readonly maxHeight = input('min(54rem, calc(100dvh - 2rem))');

  readonly filedropEnabled = input(false, { transform: booleanAttribute });
  readonly filedropDisabled = input(false, { transform: booleanAttribute });
  readonly mediaUpload = input(false, { transform: booleanAttribute });
  readonly filedropExtensions = input<readonly string[]>(['.pdf']);
  readonly filedropMaxFileSizeMb = input(10, { transform: numberAttribute });
  readonly filedropMaxFiles = input(10, { transform: numberAttribute });
  readonly filedropShowUploadQueue = input(true, { transform: booleanAttribute });
  readonly filedropMaxConcurrentUploads = input(3, { transform: numberAttribute });
  readonly filedropUploadQueueDisplayThreshold = input(3, { transform: numberAttribute });

  readonly closed = output<void>();
  readonly confirmed = output<void>();
  readonly extraClicked = output<void>();
  readonly mediaUploaded = output<readonly MediaDto[]>();
  readonly mediaUploadStarted = output<AppFiledropUploadFileEvent>();
  readonly mediaUploadProgress = output<AppFiledropUploadProgressEvent>();
  readonly mediaUploadCompleted = output<AppFiledropUploadCompletedEvent>();
  readonly mediaUploadFailed = output<AppFiledropUploadFailedEvent>();
  readonly filesSelected = output<readonly File[]>();
  readonly filesRejected = output<readonly AppFiledropRejectedFile[]>();

  openFileDialog(): void {
    this.filedrop()?.openFileDialog();
  }

  protected close(): void {
    if (this.closeDisabled()) {
      return;
    }

    this.closed.emit();
  }

  protected confirm(): void {
    if (this.confirmDisabled()) {
      return;
    }

    this.confirmed.emit();
  }

  protected extra(): void {
    if (this.extraDisabled()) {
      return;
    }

    this.extraClicked.emit();
  }

  protected onBackdropClick(event: MouseEvent): void {
    if (this.closeOnBackdrop() && event.target === event.currentTarget) {
      this.close();
    }
  }

  protected stopDialogEvent(event: Event): void {
    event.stopPropagation();
  }
}

